import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Card, Button, Typography, Input, Divider, Alert, Spin } from "antd";
import { UserOutlined, LockOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { login } from "../store/slices/authSlice"; // Import the action creator
import { setToken, setRefreshToken } from "../utils/auth";
import { RootState } from "../store";
import { useAppDispatch, useAppSelector } from "../hooks/useAppDispatch";
import { unwrapResult } from "@reduxjs/toolkit";

// 在 Login.tsx 顶部添加：
type LoginAction = {
  meta: {
    requestStatus: string;
  };
  payload: {
    token: string;
    refreshToken: string;
  };
};

const { Title } = Typography;

const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background: #f0f2f5;
`;

const StyledCard = styled(Card)`
  width: 400px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 24px;
  font-size: 24px;
  font-weight: bold;
  color: #1677ff;
`;

const StyledForm = styled(Form)`
  .form-item {
    margin-bottom: 24px;
  }

  .error-message {
    color: #ff4d4f;
    font-size: 12px;
    margin-top: 4px;
  }

  .login-button {
    width: 100%;
    height: 40px;
  }
`;

const LoginSchema = Yup.object().shape({
  username: Yup.string().required("Username is required"),
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
});

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { isLoading, error, isAuthenticated } = useAppSelector(
    (state: RootState) => state.auth,
  );

  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || "/dashboard";
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const handleSubmit = async (values: {
    username: string;
    password: string;
  }) => {
    try {
      const resultAction = await dispatch(login(values));
      const { token, refreshToken } = unwrapResult(resultAction);

      console.log("✅ token:", token); // 👈 确保这里打印出了 token
      console.log("✅ refreshToken:", refreshToken); // 👈 确保这里打印出了 refreshToken

      // Store both tokens
      setToken(token); // ✅ 把 token 存入 localStorage.kol_network_token
      setRefreshToken(refreshToken); // ✅ 把 refreshToken 存入 localStorage.kol_network_refresh_token
    } catch (err) {
      console.error("登录失败", err);
    }
  };

  return (
    <LoginContainer>
      <StyledCard>
        <Logo>KOL Network Portal</Logo>
        <Title level={4} style={{ textAlign: "center" }}>
          Admin Login
        </Title>

        <Formik
          initialValues={{ username: "", password: "" }}
          validationSchema={LoginSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <StyledForm>
              <div className="form-item">
                <Field
                  as={Input}
                  name="username"
                  placeholder="Username"
                  prefix={<UserOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
                  size="large"
                />
                <ErrorMessage
                  name="username"
                  component="div"
                  className="error-message"
                />
              </div>

              <div className="form-item">
                <Field
                  as={Input.Password}
                  name="password"
                  placeholder="Password"
                  prefix={<LockOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
                  size="large"
                />
                <ErrorMessage
                  name="password"
                  component="div"
                  className="error-message"
                />
              </div>

              {error && (
                <Alert
                  message={error}
                  type="error"
                  showIcon
                  style={{ marginBottom: 24 }}
                />
              )}

              <Button
                type="primary"
                htmlType="submit"
                className="login-button"
                disabled={isLoading}
              >
                {isLoading ? <Spin size="small" /> : "Log in"}
              </Button>
            </StyledForm>
          )}
        </Formik>
      </StyledCard>
    </LoginContainer>
  );
};

export default Login;
